import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { BarChart3, TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle2, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { FinancePageLayout, MoneyDisplay, BusinessUnitSelector } from '@/components/finance';
import type { BusinessUnit } from '@/components/finance/BusinessUnitSelector';
import { useBudgetVsActual } from '@/hooks/finance/useBudgetVsActual';

const MONTHS = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' },
];

function VarianceBadge({ variance, variancePercent }: { variance: number; variancePercent: number }) {
  const absPercent = Math.abs(variancePercent);
  
  if (absPercent <= 5) {
    return (
      <Badge variant="default" className="gap-1">
        <CheckCircle2 className="h-3 w-3" />
        {variancePercent >= 0 ? '+' : ''}{variancePercent.toFixed(1)}%
      </Badge>
    );
  }
  
  if (absPercent <= 15) {
    return (
      <Badge variant="secondary" className="gap-1 bg-yellow-500/20 text-yellow-700 dark:text-yellow-400">
        <AlertTriangle className="h-3 w-3" />
        {variancePercent >= 0 ? '+' : ''}{variancePercent.toFixed(1)}%
      </Badge>
    );
  }
  
  return (
    <Badge variant="destructive" className="gap-1">
      <AlertTriangle className="h-3 w-3" />
      {variancePercent >= 0 ? '+' : ''}{variancePercent.toFixed(1)}%
    </Badge>
  );
}

export default function BudgetVsActual() {
  const currentDate = new Date();
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [year, setYear] = useState(currentDate.getFullYear());
  const [country, setCountry] = useState<BusinessUnit>('all');

  const { data, isLoading } = useBudgetVsActual(year, month, country === 'all' ? undefined : country);

  const years = Array.from({ length: 3 }, (_, i) => currentDate.getFullYear() - 1 + i);

  const goToPreviousMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(y => y - 1);
    } else {
      setMonth(m => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(y => y + 1);
    } else {
      setMonth(m => m + 1);
    }
  };

  const statusCounts = useMemo(() => {
    if (!data) return { ok: 0, warning: 0, critical: 0 };
    return {
      ok: data.items.filter(i => i.status === 'ok').length,
      warning: data.items.filter(i => i.status === 'warning').length,
      critical: data.items.filter(i => i.status === 'critical').length,
    };
  }, [data]);

  // Items with issues (for suggestions)
  const problemItems = useMemo(() => {
    if (!data) return [];
    return data.items
      .filter(i => i.status !== 'ok' && i.budgeted > 0)
      .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance))
      .slice(0, 5);
  }, [data]);

  return (
    <FinancePageLayout>
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <h1 className="text-2xl font-bold tracking-tight">Orçado vs Realizado</h1>
        <Button variant="outline" disabled>
          <Download className="h-4 w-4 mr-2" />
          Exportar
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Período</Label>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={goToPreviousMonth}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m) => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                  <SelectTrigger className="w-[90px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={goToNextMonth}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>País</Label>
              <BusinessUnitSelector value={country} onChange={setCountry} includeAll />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Orçado</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              <MoneyDisplay value={data?.totalBudgeted || 0} currency="USD" />
            </p>
          </CardContent>
        </Card>
        <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Realizado</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                <MoneyDisplay value={data?.totalActual || 0} currency="USD" />
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Variância</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <p className={`text-2xl font-bold ${(data?.totalVariance || 0) > 0 ? 'text-destructive' : 'text-green-600'}`}>
                  <MoneyDisplay value={Math.abs(data?.totalVariance || 0)} currency="USD" />
                </p>
                {data && (
                  <Badge variant={data.totalVariance > 0 ? 'destructive' : 'default'}>
                    {data.totalVariancePercent >= 0 ? '+' : ''}{data.totalVariancePercent.toFixed(1)}%
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <div className="flex items-center gap-1">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="font-bold">{statusCounts.ok}</span>
                </div>
                <div className="flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  <span className="font-bold">{statusCounts.warning}</span>
                </div>
                <div className="flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  <span className="font-bold">{statusCounts.critical}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

      {/* Detail Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detalhamento por Conta</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Carregando...</div>
          ) : !data || data.items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum dado para o período selecionado</p>
              <p className="text-sm">Defina um orçamento e registre despesas para ver a análise.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Conta</TableHead>
                  <TableHead className="text-right">Orçado</TableHead>
                  <TableHead className="text-right">Realizado</TableHead>
                  <TableHead className="text-right">Variância</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="w-[150px]">Execução</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((item) => {
                  const executionPercent = item.budgeted > 0 
                    ? Math.min((item.actual / item.budgeted) * 100, 150) 
                    : 0;
                  
                  return (
                    <TableRow key={item.accountCode}>
                      <TableCell>
                        <div>
                          <span className="text-muted-foreground text-xs">{item.accountCode}</span>
                          <span className="ml-2 font-medium">{item.accountName}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <MoneyDisplay value={item.budgeted} currency={item.currency as 'USD' | 'BRL'} />
                      </TableCell>
                      <TableCell className="text-right">
                        <MoneyDisplay value={item.actual} currency={item.currency as 'USD' | 'BRL'} />
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={item.variance > 0 ? 'text-destructive' : 'text-green-600'}>
                          {item.variance > 0 ? '+' : ''}
                          <MoneyDisplay value={item.variance} currency={item.currency as 'USD' | 'BRL'} />
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <VarianceBadge variance={item.variance} variancePercent={item.variancePercent} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress 
                            value={executionPercent} 
                            className="h-2"
                          />
                          <span className="text-xs text-muted-foreground w-10">
                            {executionPercent.toFixed(0)}%
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Suggestions */}
      {problemItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Ações Sugeridas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {problemItems.map((item) => (
                <div key={item.accountCode} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <Badge variant={item.status === 'critical' ? 'destructive' : 'secondary'}>
                    {item.status === 'critical' ? '❌' : '⚠️'}
                  </Badge>
                  <div className="flex-1">
                    <p className="font-medium">{item.accountName}</p>
                    <p className="text-sm text-muted-foreground">
                      {item.variance > 0 
                        ? `Estourou o orçamento em ${item.variancePercent.toFixed(1)}% (${item.currency} ${Math.abs(item.variance).toLocaleString()})`
                        : `Economizou ${Math.abs(item.variancePercent).toFixed(1)}% (${item.currency} ${Math.abs(item.variance).toLocaleString()})`
                      }
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      <div className="text-sm text-muted-foreground flex flex-wrap gap-6">
        <div className="flex items-center gap-2">
          <Badge variant="default" className="gap-1">
            <CheckCircle2 className="h-3 w-3" />
            OK
          </Badge>
          <span>Dentro do orçamento (±5%)</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="gap-1 bg-yellow-500/20 text-yellow-700">
            <AlertTriangle className="h-3 w-3" />
            Atenção
          </Badge>
          <span>Variação de 5-15%</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="destructive" className="gap-1">
            <AlertTriangle className="h-3 w-3" />
            Crítico
          </Badge>
          <span>Variação acima de 15%</span>
        </div>
      </div>
    </FinancePageLayout>
  );
}
